<template>
  <div class="settlement-attachments-container">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>附件管理</span>
      </div>
      <div class="header-actions">
        <el-button @click="goBack" icon="el-icon-arrow-left">返回</el-button>
      </div>
    </div>
    
    <!-- 结算单信息 -->
    <div class="settlement-info-bar">
      <span class="settlement-info">结算单号：{{ settlementInfo.settlementNumber || settlementInfo.settlement_no || '-' }}</span>
      <span class="settlement-info">结算标题：{{ settlementInfo.settlementTitle || settlementInfo.settlement_title || '-' }}</span>
      <span class="settlement-info">结算状态：
        <el-tag :type="getStatusTagType(settlementInfo.status)">{{ getStatusText(settlementInfo.status) }}</el-tag>
      </span>
      <el-alert
        v-if="settlementInfo.status === 'INVOICED'"
        title="已开票状态不允许删除附件"
        type="warning"
        :closable="false"
        show-icon
        style="margin-top: 10px;">
      </el-alert>
    </div>
    
    <!-- 搜索区域 -->
    <div class="search-area">
      <el-input
        v-model="searchParams.originalName"
        placeholder="文件名"
        clearable
        class="search-input"
        prefix-icon="el-icon-search"
        @keyup.enter.native="handleSearch"
      ></el-input>
      
      <el-select v-model="searchParams.fileType" placeholder="文件类型" clearable class="search-input">
        <el-option label="PDF" value="pdf"></el-option>
        <el-option label="Word" value="doc"></el-option>
        <el-option label="Excel" value="xls"></el-option>
        <el-option label="图片" value="jpg"></el-option>
        <el-option label="压缩文件" value="zip"></el-option>
      </el-select>
      
      <el-upload
        ref="upload"
        :http-request="customUpload"
        :before-upload="beforeUpload"
        :show-file-list="false"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip,.rar"
        class="upload-component"
      >
        <el-button v-permission="'settlement:settlement-attachments:create'" type="success" icon="el-icon-upload" style="margin-top: 10px;">上传附件</el-button>
      </el-upload>
      
      <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
      <el-button @click="resetSearch" icon="el-icon-refresh">重置</el-button>
    </div>
    
    <!-- 附件列表 -->
    <el-table
      :data="attachmentList"
      border
      style="width: 100%"
      v-loading="loading"
      :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
    >
      <el-table-column
        label="附件名称"
        min-width="200"
        align="center"
      >
        <template slot-scope="scope">
          <div class="file-name">
            <i :class="getFileIcon(scope.row.fileExtension)"></i>
            <span>{{ scope.row.originalName }}</span>
          </div>
        </template>
      </el-table-column>
      
      <el-table-column
        label="文件大小"
        width="120"
        align="center"
      >
        <template slot-scope="scope">
          {{ formatFileSize(scope.row.fileSize) }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="文件类型"
        width="120"
        align="center"
      >
        <template slot-scope="scope">
          {{ scope.row.fileExtension || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="上传人"
        width="120"
        align="center"
      >
        <template slot-scope="scope">
          {{ scope.row.uploaderName || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="上传时间"
        width="180"
        align="center"
      >
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.uploadTime) }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="描述"
        min-width="150"
        align="center"
      >
        <template slot-scope="scope">
          {{ scope.row.description || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="操作"
        width="200"
        align="center"
        fixed="right"
      >
        <template slot-scope="scope">
          <el-button
            v-permission="'settlement:settlement-attachments:download'"
            type="primary"
            size="mini"
            @click="downloadAttachment(scope.row)"
            icon="el-icon-download"
          >下载</el-button>
          
          <el-button
            v-if="canDelete(scope.row)"
            type="danger"
            size="mini"
            v-permission="'settlement:settlement-attachments:delete'"
            @click="deleteAttachment(scope.row)"
            icon="el-icon-delete"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.current"
        :page-sizes="[10, 20, 30, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
      ></el-pagination>
    </div>
    
    <!-- 空状态 -->
    <div v-if="!loading && attachmentList.length === 0" class="empty-state">
      <i class="el-icon-document"></i>
      <p>暂无附件</p>
    </div>
  </div>
</template>

<script>
import { 
  upload_settlement_attachment,
  get_settlement_attachments_pagination,
  download_settlement_attachment, 
  delete_settlement_attachment 
} from '@/api/modules/settlement-attachment'
import { query_settlement_by_id } from '@/api/modules/settlement'

export default {
  name: 'SettlementAttachments',
  data() {
    return {
      // 结算单ID
      settlementId: null,
      // 结算单信息
      settlementInfo: {},
      // 附件列表
      attachmentList: [],
      // 加载状态
      loading: false,
      // 当前用户ID
      currentUserId: null,
      // 分页参数
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0
      },
      // 搜索参数
      searchParams: {
        fileType: '',
        originalName: ''
      }
    };
  },
  created() {
    // 获取结算单ID
    this.settlementId = this.$route.params.id;
    if (!this.settlementId) {
      this.$message.error('结算单ID不能为空');
      this.$router.go(-1);
      return;
    }
    
    // 获取当前用户ID
    this.getCurrentUserId();
    // 获取结算单信息
    this.getSettlementInfo();
    // 获取附件列表
    this.getAttachmentList();
  },
  methods: {
    // 获取当前用户ID
    getCurrentUserId() {
      try {
        const store = this.$store || {};
        const state = store.state || {};
        
        let userInfo = null;
        
        if (state.user && state.user.userInfo) {
          userInfo = state.user.userInfo;
        } else if (state.userInfo) {
          userInfo = state.userInfo;
        } else if (state.auth && state.auth.userInfo) {
          userInfo = state.auth.userInfo;
        }
        
        if (!userInfo || (!userInfo.id && !userInfo.userId)) {
          const localUserInfo = localStorage.getItem('userInfo');
          if (localUserInfo) {
            try {
              userInfo = JSON.parse(localUserInfo);
            } catch (e) {
              console.error('解析localStorage中的userInfo失败:', e);
            }
          }
        }
        
        if (userInfo && (userInfo.id || userInfo.userId)) {
          this.currentUserId = userInfo.id || userInfo.userId;
        } else {
          this.currentUserId = null;
        }
      } catch (err) {
        console.error('获取用户ID出错:', err);
        this.currentUserId = null;
      }
    },
    
    // 自定义上传方法
    customUpload(options) {
// 检查必要参数
      if (!this.settlementId) {
        this.$message.error('结算单ID不能为空');
        options.onError(new Error('结算单ID不能为空'));
        return;
      }
      
      if (!this.currentUserId) {
        this.$message.error('用户ID不能为空，请重新登录');
        options.onError(new Error('用户ID不能为空'));
        return;
      }
      
      const formData = new FormData();
      formData.append('file', options.file);
      formData.append('settlementId', this.settlementId);
      formData.append('uploaderId', this.currentUserId);
      
return upload_settlement_attachment(formData)
        .then(response => {
if (response.data && response.data.success) {
            this.$message.success('附件上传成功');
            this.getAttachmentList(); // 重新获取附件列表
            options.onSuccess(response.data);
          } else {
            this.$message.error(response.data?.message || '附件上传失败');
            options.onError(new Error(response.data?.message || '附件上传失败'));
          }
        })
        .catch(error => {
          console.error('附件上传失败:', error);
          this.$message.error('附件上传失败，请稍后重试');
          options.onError(error);
        });
    },
    
    // 获取结算单信息
    getSettlementInfo() {
      query_settlement_by_id(this.settlementId)
        .then(response => {
          if (response.data && response.data.success) {
            this.settlementInfo = response.data.data || {};
          } else {
            console.error('获取结算单信息失败:', response.data?.message);
          }
        })
        .catch(error => {
          console.error('获取结算单信息请求错误:', error);
        });
    },
    
    // 获取附件列表
    getAttachmentList() {
      this.loading = true;
      
      // 构建查询参数
      const params = {
        settlementId: this.settlementId,
        page: this.pagination.current - 1, // 转换为0基页码
        size: this.pagination.pageSize,
        sortBy: 'uploadTime',
        sortDirection: 'desc'
      };
      
      // 添加搜索条件
      if (this.searchParams.fileType) {
        params.fileType = this.searchParams.fileType;
      }
      if (this.searchParams.originalName) {
        params.originalName = this.searchParams.originalName;
      }
      
      get_settlement_attachments_pagination(params)
        .then(response => {
// 处理分页响应格式
          if (response.data && response.data.content) {
            this.attachmentList = response.data.content || [];
            this.pagination.total = response.data.totalElements || 0;
            this.pagination.current = (response.data.page || 0) + 1; // 转换为1基页码
          } else if (Array.isArray(response.data)) {
            this.attachmentList = response.data;
            this.pagination.total = response.data.length;
          } else {
            console.error('获取附件列表失败:', response.data?.message);
            this.$message.error(response.data?.message || '获取附件列表失败');
            this.attachmentList = [];
            this.pagination.total = 0;
          }
          this.loading = false;
        })
        .catch(error => {
          console.error('获取附件列表请求错误:', error);
          this.$message.error('获取附件列表失败，请稍后重试');
          this.attachmentList = [];
          this.pagination.total = 0;
          this.loading = false;
        });
    },
    
    // 上传前检查
    beforeUpload(file) { // eslint-disable-line no-unused-vars
      // 检查文件大小（50MB限制）
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$message.error('文件大小不能超过50MB');
        return false;
      }
      
      // 检查文件类型
      const allowedTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'zip', 'rar'];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        this.$message.error('不支持的文件类型，请上传PDF、Word、Excel、图片或压缩文件');
        return false;
      }
      
      return true;
    },
    
    
    // 下载附件
    downloadAttachment(attachment) {
      download_settlement_attachment(attachment.id)
        .then(response => {
          // 创建下载链接
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.download = attachment.originalName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(error => {
          console.error('下载附件失败:', error);
          this.$message.error('下载附件失败，请稍后重试');
        });
    },
    
    // 删除附件
    deleteAttachment(attachment) {
      this.$confirm('确认删除该附件?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        delete_settlement_attachment(attachment.id, this.currentUserId)
          .then(response => {
            if (response.data && response.data.success) {
              this.$message.success('附件删除成功');
              this.getAttachmentList(); // 重新获取附件列表
            } else {
              this.$message.error(response.data?.message || '附件删除失败');
            }
          })
          .catch(error => {
            console.error('删除附件失败:', error);
            this.$message.error('删除附件失败，请稍后重试');
          });
      }).catch(() => {
        // 用户取消操作
      });
    },
    
    // 判断是否可以删除
    canDelete(attachment) {
      // 已开票状态不允许删除附件
      if (this.settlementInfo.status === 'INVOICED') {
        return false;
      }
      // 只有上传者可以删除自己的附件
      return this.currentUserId && attachment.uploaderId === this.currentUserId;
    },
    
    // 获取文件图标
    getFileIcon(extension) {
      if (!extension) return 'el-icon-document';
      
      const ext = extension.toLowerCase();
      if (['pdf'].includes(ext)) return 'el-icon-document';
      if (['doc', 'docx'].includes(ext)) return 'el-icon-document';
      if (['xls', 'xlsx'].includes(ext)) return 'el-icon-s-grid';
      if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'el-icon-picture';
      if (['zip', 'rar'].includes(ext)) return 'el-icon-folder';
      
      return 'el-icon-document';
    },
    
    // 格式化文件大小
    formatFileSize(size) {
      if (!size) return '-';
      
      const units = ['B', 'KB', 'MB', 'GB'];
      let index = 0;
      let fileSize = size;
      
      while (fileSize >= 1024 && index < units.length - 1) {
        fileSize /= 1024;
        index++;
      }
      
      return `${fileSize.toFixed(1)} ${units[index]}`;
    },
    
    // 格式化日期时间
    formatDateTime(dateString) {
      if (!dateString) return '-';
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return dateString || '-';
        }
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}`;
      } catch (e) {
        console.error('日期时间格式化错误:', e);
        return dateString || '-';
      }
    },
    
    // 页码改变
    handleCurrentChange(currentPage) {
      this.pagination.current = currentPage;
      this.getAttachmentList();
    },
    
    // 每页条数改变
    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize;
      this.pagination.current = 1;
      this.getAttachmentList();
    },
    
    // 搜索
    handleSearch() {
      this.pagination.current = 1;
      this.getAttachmentList();
    },
    
    // 重置搜索
    resetSearch() {
      this.searchParams = {
        fileType: '',
        originalName: ''
      };
      this.pagination.current = 1;
      this.getAttachmentList();
    },
    
    // 根据状态获取Tag类型
    getStatusTagType(status) {
      if (!status) return 'info';
      
      switch (status) {
        case 'ACTIVE':
          return 'info';
        case 'EXECUTING':
          return 'warning';
        case 'INVOICED':
          return 'primary';
        case 'FINISHED':
          return 'success';
        case 'CANCELLED':
          return 'danger';
        default:
          return 'info';
      }
    },
    
    // 获取状态显示文本
    getStatusText(status) {
      if (!status) return '-';
      
      switch (status) {
        case 'ACTIVE':
          return '待确认';
        case 'EXECUTING':
          return '已确认';
        case 'INVOICED':
          return '已开票';
        case 'FINISHED':
          return '结算完成';
        case 'CANCELLED':
          return '已取消';
        default:
          return status;
      }
    },
    
    // 返回结算列表
    goBack() {
      this.$router.push('/settlement/settlements-list');
    }
  }
};
</script>

<style lang="scss" scoped>
.settlement-attachments-container {
  padding: 20px;
  background-color: #fff;
  
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    .header-title {
      display: flex;
      align-items: center;
      
      .title-mark {
        width: 4px;
        height: 18px;
        background-color: #2c73eb;
        margin-right: 8px;
      }
      
      span {
        font-size: 18px;
        font-weight: 500;
        color: #303133;
      }
    }
  }
  
  .settlement-info-bar {
    margin-bottom: 20px;
    padding: 12px 16px;
    background-color: #f5f7fa;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    
    .settlement-info {
      font-size: 14px;
      color: #606266;
    }
  }
  
  .search-area {
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    
    .search-input {
      width: 200px;
      margin-right: 15px;
      margin-bottom: 10px;
    }
    
    .upload-component {
      margin-right: 15px;
      margin-bottom: 10px;
    }
    
    .el-button {
      margin-bottom: 10px;
    }
  }
  
  .pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: center;
  }
  
  .file-name {
    display: flex;
    align-items: center;
    
    i {
      margin-right: 8px;
      font-size: 16px;
      color: #409EFF;
    }
    
    span {
      flex: 1;
      word-break: break-all;
    }
  }
  
  .empty-state {
    text-align: center;
    padding: 60px 0;
    color: #909399;
    
    i {
      font-size: 48px;
      margin-bottom: 16px;
      display: block;
    }
    
    p {
      font-size: 16px;
      margin: 0;
    }
  }
}
</style>
